import { motion } from "framer-motion";
import { IMAGES, waLink, WA_MESSAGES } from "@/lib/site";
import { MessageCircle, Truck, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

const CONTENTS = [
    "Sehri Mug & Iftaar Mug — custom printed",
    "Premium dates",
    "Zam Zam water",
    "Dua-a-Day booklet",
];

export const Promo = () => (
    <section id="special" className="px-4 py-16 sm:px-6 md:py-24" data-testid="promo-banner">
        <motion.div
            {...fadeUp}
            transition={{ duration: 0.8, ease: EASE }}
            className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] border border-sky-300 bg-sky-200 text-sakura-900 shadow-[0_40px_100px_rgba(162,182,208,0.4)]"
        >
            <div
                aria-hidden="true"
                className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-sakura-400/40 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/60 blur-3xl"
            />

            <div className="relative grid grid-cols-1 items-center gap-10 p-7 sm:p-12 lg:grid-cols-2 lg:gap-14 lg:p-16">
                <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-sky-600/30 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.26em] text-sky-700">
                        <Sparkles size={13} />
                        This Month's Special
                    </span>

                    <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-sakura-900 sm:text-5xl lg:text-6xl">
                        Ramadhaan
                        <span className="block italic text-sakura-600">
                            Gift Sets
                        </span>
                    </h2>

                    <p className="mt-6 max-w-md text-base leading-relaxed text-sakura-800">
                        A complete month of blessings in one basket —
                        beautifully packed and personalized with your family's
                        names.
                    </p>

                    <ul className="mt-7 space-y-3.5">
                        {CONTENTS.map((item, i) => (
                            <motion.li
                                key={item}
                                initial={{ opacity: 0, x: -18 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, amount: 0.4 }}
                                transition={{ duration: 0.55, delay: 0.1 + i * 0.09, ease: EASE }}
                                className="flex items-center gap-3 text-[15px] text-sakura-900/90"
                            >
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/70">
                                    <span className="h-2 w-2 rounded-full bg-sky-600" />
                                </span>
                                {item}
                            </motion.li>
                        ))}
                    </ul>

                    <div className="mt-9 flex flex-wrap items-center gap-4">
                        <a
                            data-testid="promo-cta"
                            href={waLink(WA_MESSAGES.ramadhaan)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2.5 rounded-full bg-sky-600 px-7 py-4 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-200"
                        >
                            <MessageCircle size={18} />
                            Enquire on WhatsApp
                        </a>
                    </div>

                    <p className="mt-7 flex items-center gap-2 text-sm text-sakura-800/85">
                        <Truck size={16} className="text-sky-700" />
                        Courier cost is not included — quoted separately by
                        area.
                    </p>
                </div>

                <div className="relative">
                    <motion.a
                        href={waLink(WA_MESSAGES.ramadhaan)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="promo-image-link"
                        initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="block cursor-pointer overflow-hidden rounded-[2rem] border-4 border-white/80 shadow-[0_30px_70px_rgba(107,133,168,0.4)] hover:scale-[1.015] transition-transform duration-300"
                    >
                        <img
                            src={IMAGES.ramadhaan}
                            alt="Ramadhaan gift basket with Iftaar and Sehri mugs, Zam Zam water and dates — tap to order on WhatsApp"
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </motion.a>
                    <div className="absolute -bottom-4 -right-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-700 shadow-xl sm:right-6">
                        Limited monthly edition
                    </div>
                </div>
            </div>
        </motion.div>
    </section>
);

export default Promo;
